<template>
  <div>
    <div class="dashboard-container">
      <div class="dashboard-header">
        <h2 class="dashboard-title">All Users</h2>
      </div>
    </div>

    <template v-if="loading">
      <loading-spinner
        :blur="true"
        v-if="loading"
        :showLabel="true"
        label="Loading..."
      />
    </template>

    <div v-else class="table-container">
      <div class="d-flex justify-content-end mb-2">
        <b-button
          type="button"
          @click="handleNewToken"
          variant="custom"
          class="btn-new"
        >
          New
        </b-button>
      </div>
      <b-table
        @row-clicked="onRowClicked"
        :fields="fields"
        :items="items"
        hover
        class="styled-table"
      >
        <template #cell(role)="data">
          <span>{{
            data.item.role === 0
              ? "Root"
              : data?.item.role === 1
              ? "Admin"
              : "User"
          }}</span>
        </template>
      </b-table>
    </div>
    <b-modal
      id="edit-user-modal"
      title="Edit User"
      size="lg"
      centered
      @ok="handleSave"
      v-model="openEditModal"
      ok-title="Save Changes"
      cancel-title="Cancel"
      header-bg-variant="light"
      footer-bg-variant="light"
      hide-header-close
    >
      <template #modal-title>
        <div class="d-flex align-items-center">
          <i class="fas fa-user-edit mr-2"></i>
          <span>Edit User Details</span>
        </div>
      </template>

      <loading-spinner
        :blur="true"
        v-if="editUserLoading"
        :showLabel="true"
        label="Updating..."
      />

      <b-form class="px-2">
        <b-row class="mb-4">
          <b-col md="6">
            <b-form-group label="Name" label-for="name-input">
              <b-form-input
                id="name-input"
                v-model="editForm.name"
                placeholder="Enter user's name"
              />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Username" label-for="username-input">
              <b-form-input
                id="username-input"
                v-model="editForm.username"
                placeholder="Enter username"
              />
            </b-form-group>
          </b-col>

          <b-col md="6">
            <b-form-group label="Email" label-for="email-input">
              <b-form-input
                type="email"
                id="email-input"
                v-model="editForm.email"
                placeholder="Enter email address"
              />
            </b-form-group>
          </b-col>
          <b-col md="3">
            <b-form-group label="Role" label-for="role-select">
              <b-form-select
                id="role-select"
                v-model="editForm.role"
                :options="roleOptions"
              />
            </b-form-group>
          </b-col>
          <b-col md="3">
            <b-form-group label="Status" label-for="status-input">
              <b-form-input id="status-input" v-model="editForm.status" />
            </b-form-group>
          </b-col>
        </b-row>

        <div class="permissions-section bg-light p-3 rounded mb-3">
          <h6 class="mb-3 text-muted">User Permissions</h6>
          <b-row>
            <b-col md="4" class="mb-2">
              <b-form-checkbox v-model="editForm.allowReels">
                Allow Reels
              </b-form-checkbox>
            </b-col>
            <b-col md="4" class="mb-2">
              <b-form-checkbox v-model="editForm.allowPhotos">
                Allow Photos
              </b-form-checkbox>
            </b-col>
            <b-col md="4" class="mb-2">
              <b-form-checkbox v-model="editForm.allowServerStats">
                Allow Server Stats
              </b-form-checkbox>
            </b-col>
            <b-col md="4" class="mb-2">
              <b-form-checkbox v-model="editForm.allowGeneralStats">
                Allow General Stats
              </b-form-checkbox>
            </b-col>
            <b-col md="4" class="mb-2">
              <b-form-checkbox v-model="editForm.allowCustomProxy">
                Allow Custom Proxy
              </b-form-checkbox>
            </b-col>
            <b-col md="4" class="mb-2">
              <b-form-checkbox v-model="editForm.allowCollaborations">
                Allow Collaborations
              </b-form-checkbox>
            </b-col>
            <b-col md="4" class="mb-2">
              <b-form-checkbox v-model="editForm.allowStoryPost">
                Allow Story Posts
              </b-form-checkbox>
            </b-col>
          </b-row>
        </div>
      </b-form>

      <template #modal-footer="{ ok, cancel }">
        <b-button variant="secondary" @click="cancel()"> Cancel </b-button>
        <b-button variant="primary" @click="ok()" :disabled="editUserLoading">
          <i class="fas fa-save mr-1"></i> Save Changes
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<style scoped>
.b-modal .modal-content {
  border-radius: 12px;
}

.custom-control {
  margin: 15px 0 !important;
}

.b-form-checkbox {
  font-size: 0.9rem;
  margin-right: 10px;
}
.custom-control-input {
  margin-right: 10px;
}
.btn-new {
  background: #00a2fd !important;
  background: #00a2fd !important;
  color: #fff !important;
  width: 150px;
  font-size: 16px !important;
  font-weight: 600 !important;
}

.status-text {
  color: #fff;
  text-align: center;
}

.status-done {
  background: #007200;
  border-radius: 5px;
}

.status-running {
  background: #2196f3;
  border-radius: 5px;
}

.status-error {
  background: #dd1c1a;
  border-radius: 5px;
}

/* Main container */
.dashboard-container {
  background-color: #f8f9fa;
  padding: 24px;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease-in-out;
  text-align: center;
  margin-bottom: 15px;
}

/* Title */
.dashboard-title {
  font-size: 1.5rem;
  font-weight: bold;
  color: #222;
  margin-bottom: 16px;
}

/* Sorting dropdown */
.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 1rem;
  color: #333;
  margin-bottom: 12px;
}

.sort-select {
  width: 120px;
}

/* Table container */
.table-container {
  padding: 16px;
  border-radius: 8px;
  background-color: #ffffff;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

/* Table */
.styled-table {
  border-collapse: collapse;
  width: 100%;
  border: 1px solid;
}

.styled-table th {
  background-color: #050505 !important;
  color: #f7f7f7 !important;
  text-align: left;
  padding: 12px;
  font-size: 1.1rem;
  font-weight: bold;
}

.styled-table td {
  padding: 12px;
  font-size: 1rem;
  color: #333;
  border-bottom: 1px solid #e0e0e0;
}

/* Hover Effect */
.styled-table tbody tr {
  transition: background 0.2s ease-in-out, transform 0.1s ease-in-out;
}

.styled-table tbody tr:hover {
  background-color: #f3f3f3;
  cursor: pointer;
  transform: scale(1.015);
}

/* Modal Styling */
::v-deep .modal-content {
  border: none;
  border-radius: 12px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
}

::v-deep .modal-header {
  background-color: #f8f9fa;
  border-bottom: 1px solid #eee;
  padding: 1.5rem;
}

::v-deep .modal-title {
  font-weight: 600;
  font-size: 1.25rem;
  color: #2c3e50;
}

::v-deep .modal-body {
  padding: 1.5rem;
}

::v-deep .modal-footer {
  border-top: 1px solid #eee;
  padding: 1rem 1.5rem;
  background-color: #f8f9fa;
}

::v-deep .btn-primary {
  background: #00a2fd;
  border: none;
  padding: 0.5rem 1.5rem;
  font-weight: 600;
  transition: all 0.2s ease;
}

::v-deep .btn-primary:hover {
  background: #0091e4;
  transform: translateY(-1px);
}

::v-deep .btn-secondary {
  background: #f8f9fa;
  border: 1px solid #ddd;
  color: #666;
  padding: 0.5rem 1.5rem;
  transition: all 0.2s ease;
}

::v-deep .btn-secondary:hover {
  background: #e9ecef;
}

/* Form Styling */
::v-deep .form-group {
  margin-bottom: 1.25rem;
}

::v-deep .form-group label {
  font-weight: 500;
  color: #2c3e50;
  margin-bottom: 0.5rem;
}

::v-deep .form-control {
  border-radius: 8px;
  border: 1px solid #ddd;
  padding: 0.625rem;
  transition: all 0.2s ease;
}

::v-deep .form-control:focus {
  border-color: #00a2fd;
  box-shadow: 0 0 0 0.2rem rgba(0, 162, 253, 0.15);
}

::v-deep .custom-select {
  border-radius: 8px;
  border: 1px solid #ddd;
  padding: 0.625rem;
}

::v-deep .form-check {
  padding: 0.5rem;
  transition: all 0.2s ease;
}

::v-deep .form-check:hover {
  background: #f8f9fa;
  border-radius: 6px;
}

::v-deep .form-check-label {
  color: #4a5568;
  font-size: 0.95rem;
  cursor: pointer;
}

::v-deep .form-check-input {
  margin-right: 0.5rem;
}

/* Mobile */
@media (max-width: 767px) {
  .dashboard-container {
    padding: 16px;
  }

  .dashboard-title {
    font-size: 1.2rem;
  }

  .table-container {
    padding: 10px;
  }
}
</style>
<script>
import router from "@/router";
import axios from "axios";
import moment from "moment";
import LoadingSpinner from "../../components/Globals/LoadingSpinner.vue";

export default {
  components: { LoadingSpinner },
  data() {
    return {
      sortOption: "Newest",
      sortOptions: ["Newest", "Oldest", "Status", "Name"],
      loading: true,
      editUserLoading: false,
      items: [],
      fields: [
        "name",
        "username",
        "email",
        "role",
        "status",
        {
          key: "createdAt",
          label: "Created On",
        },
      ],
      editForm: {
        name: "",
        username: "",
        email: "",
        role: 0,
        status: 0,
        createdAt: "",
        allowReels: false,
        allowPhotos: false,
        allowServerStats: false,
        allowGeneralStats: false,
        allowCustomProxy: false,
        allowCollaborations: false,
        allowStoryPost: false,
      },
      roleOptions: [
        { value: 0, text: "Root" },
        { value: 1, text: "Admin" },
        { value: 2, text: "User" },
      ],
      openEditModal: false,
    };
  },
  methods: {
    handleNewToken() {
      this.openEditModal = true;
    },
    async onRowClicked(item) {
      this.editForm = { ...item };
      this.openEditModal = true;
    },
    formatNumber(num) {
      return new Intl.NumberFormat().format(num);
    },
    onOpenEditModal(item) {
      this.editForm = { ...item };
      this.openEditModal = true;
    },

    async handleSave() {
      this.editUserLoading = true;
      this.$toast.info("Saving User...");

      const response = await axios.post("/api/users/update", {
        ...this.editForm,
        userId: this.editForm._id,
      });

      if (response.status !== 200) {
        this.$toast.error("Failed to update user");
        this.editUserLoading = false;
        return;
      }

      this.$toast.success("User Updated");
      setTimeout(() => {
        window.location.reload();
      }, 500);
      this.loading = true;
      this.openEditModal = false;
    },
  },
  async mounted() {
    try {
      this.loading = false;
      const response = await axios.post("/api/users/all"); // Replace with your API URL
      const _itemsAll = response.data.data;
      const mappedItems = _itemsAll.map((item) => {
        return {
          ...item,
          createdAt:
            item?.createdAt &&
            moment(item?.createdAt).format("DD-MM-YYYY hh:mm A") +
              " ( UK Time )",
        };
      });
      this.items = mappedItems;
    } catch {
      this.$toast.error("Failed to load data");
    }
  },
};
</script>
